import React, { useContext, useEffect, useState } from "react";
import OverviewTabContent from "features/newMarketplace/GroupExtraDetails/Tabs/OverviewTab/OverviewTabContent";
import ReviewsTabContent from "features/newMarketplace/GroupExtraDetails/Tabs/ReviewsTab/ReviewsTabContent";
import Empty from "common/Empty/Empty";
import {
  MARKETPLACE_GROUP_PATH,
  MAX_BACKTEST_DRAWER_EXTENSION_SCREEN,
} from "common/constants";
import { useHistory, useRouteMatch } from "react-router-dom";
import useQuery from "utils/hooks/useQuery";
import { getGroupExtraDetailsRoute, getRouteWithoutPath } from "utils/routes";
import useTabsWithUrl from "common/Tabs/useTabsWithUrl";
import ExtraDetailsDrawer from "common/ExtraDetails/ExtraDetailsDrawer";
import DrawerExtension from "features/newMarketplace/GroupExtraDetails/DrawerExtension";
import { GROUP_DRAWER_EXTENSION_WIDTH } from "features/newMarketplace/common/constants";
import useMedia from "utils/hooks/useMedia";
import ScoreInfo from "features/newMarketplace/common/ScoreInfo";
import BacktestingStatistics from "features/bots/BotForm/Backtesting/ExtraDetailsDrawer/BacktestingStatistics";
import BacktestDetailsProvider from "@shared/features/bots/BacktestDetailsProvider";
import SubGroupDetailsProvider from "@shared/features/newMarketplace/SubGroupDetailsProvider";
import { SubGroupDetailsContext } from "@shared/features/newMarketplace/SubGroupDetails.context";
import GroupInfo from "features/newMarketplace/GroupExtraDetails/Info/GroupInfo";
import { useDrawerDelayedUnmount } from "utils/hooks/misc";
import Disclaimer from "features/newMarketplace/GroupExtraDetails/Disclaimer";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { useTheme } from "styled-components";
import GroupHeaderSkeleton from "features/newMarketplace/NewLook/GroupHeaderSkeleton.tsx";
import GroupsListMessage from "features/newMarketplace/NewLook/GroupsListMessage.tsx";
import { NewLookOverviewSkeleton } from "features/newMarketplace/NewLook/NewLookOverviewStatistics.tsx";
import { GroupCompactTitle } from "features/newMarketplace/NewLook/NewLookGroupParts";

const GroupDrawer = () => {
  const match = useRouteMatch(MARKETPLACE_GROUP_PATH);
  const query = useQuery();
  const pos = query.get("pos");
  const { subGroupId: subGroupIdStr } = (match?.params || {}) as {
    subGroupId: string;
  };
  const groupId = subGroupIdStr ? parseInt(subGroupIdStr) : undefined;
  const { id: drawerGroupId, isUnmounting } = useDrawerDelayedUnmount(groupId);

  return (
    <SubGroupDetailsProvider groupId={drawerGroupId}>
      <SubGroupDrawerContent
        visible={!!groupId && !isUnmounting}
        groupPagePosition={pos ? parseInt(pos) : undefined}
      />
    </SubGroupDetailsProvider>
  );
};

export default GroupDrawer;

interface SubGroupDrawerContentProps {
  groupPagePosition?: number;
  visible: boolean;
}

const SubGroupDrawerContent = ({
  groupPagePosition,
  visible,
}: SubGroupDrawerContentProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { isNewLook } = useTheme();
  const {
    groupId,
    groupOverview,
    getGroupOverviewQuery,
    getGroupDataQuery,
    groupData,
  } = useContext(SubGroupDetailsContext)!;
  const history = useHistory();
  const isSmallScreen = useMedia(
    [`(max-width: ${MAX_BACKTEST_DRAWER_EXTENSION_SCREEN})`],
    [true],
    false
  );
  const [defaultTab, setDefaultTab] = useState(GroupExtraDetailsTabs.OVERVIEW);
  const [isScoreHover, setIsScoreHover] = useState<boolean | undefined>(false);

  const tabToDisplayName = {
    [GroupExtraDetailsTabs.OVERVIEW]: t("drawer.tabs.overview"),
    [GroupExtraDetailsTabs.REVIEWS]: t("drawer.tabs.reviews"),
    [GroupExtraDetailsTabs.SCORE]: t("drawer.tabs.score"),
    [GroupExtraDetailsTabs.STATISTICS]: t("drawer.tabs.statistics"),
  };

  const {
    tabDisplayName,
    onChange: onTabChange,
    tab,
  } = useTabsWithUrl<GroupExtraDetailsTabs>({
    tabToDisplayName,
    tabs: Object.values(GroupExtraDetailsTabs),
    path: MARKETPLACE_GROUP_PATH,
    getPathFromTab: (tab, location) => {
      if (groupId) {
        return getGroupExtraDetailsRoute(
          location,
          groupId?.toString() || "",
          tab
        );
      }
    },
    skipUrlInitialization: !visible,
    defaultTab,
  });

  const setTab = (tab: GroupExtraDetailsTabs) => {
    history.push(
      getGroupExtraDetailsRoute(
        getRouteWithoutPath(location.pathname, MARKETPLACE_GROUP_PATH),
        groupId?.toString() || "",
        tab
      )
    );
  };

  useEffect(() => {
    if (visible) {
      setDefaultTab(GroupExtraDetailsTabs.OVERVIEW);
    }
  }, [visible]);

  useEffect(() => {
    if (tab) {
      setDefaultTab?.(tab);
    }
  }, [tab]);

  useEffect(() => {
    if (
      !isSmallScreen &&
      (tab === GroupExtraDetailsTabs.SCORE ||
        tab === GroupExtraDetailsTabs.STATISTICS)
    ) {
      setTab(GroupExtraDetailsTabs.OVERVIEW);
    }
  }, [isSmallScreen, tab]);

  const onClose = () => {
    history.push(
      getRouteWithoutPath(location.pathname, MARKETPLACE_GROUP_PATH)
    );
  };

  const isLoadingGroup =
    (getGroupOverviewQuery?.isFetching || getGroupDataQuery?.isFetching) &&
    (!groupData || groupOverview?.group_id !== groupId);
  const groupInfo = groupOverview && groupData && (
    <GroupInfo
      subGroupOverview={groupOverview}
      groupPagePosition={groupPagePosition}
      userGroupData={groupData}
      setIsScoreHover={setIsScoreHover}
      onClickScore={
        isSmallScreen ? () => setTab(GroupExtraDetailsTabs.SCORE) : undefined
      }
    />
  );

  const title = isNewLook ? (
    isLoadingGroup ? (
      <GroupHeaderSkeleton />
    ) : (
      groupInfo
    )
  ) : (
    <div style={{ minHeight: 140 }}>
      <LoadingSpinnerOverlay
        isLoading={
          getGroupOverviewQuery?.isFetching || getGroupDataQuery?.isFetching
        }
      >
        {groupInfo}
      </LoadingSpinnerOverlay>
    </div>
  );

  const disclaimer = <Disclaimer style={{ marginBottom: 16 }} />;

  const tabToComponent: {
    [key in GroupExtraDetailsTabs]?: React.JSX.Element;
  } = {
    [tabToDisplayName[GroupExtraDetailsTabs.OVERVIEW]]: isNewLook ? (
      <>
        {isSmallScreen && disclaimer}
        {getGroupOverviewQuery?.isFetching &&
        groupOverview?.group_id !== groupId ? (
          <NewLookOverviewSkeleton />
        ) : (
          <OverviewTabContent />
        )}
      </>
    ) : (
      <>
        <LoadingSpinnerOverlay
          isLoading={getGroupOverviewQuery?.isFetching}
          hideContentWhileLoading={true}
          spinnerSize="large"
        >
          {isSmallScreen && disclaimer}
          <OverviewTabContent />
        </LoadingSpinnerOverlay>
      </>
    ),
  };

  if (isSmallScreen) {
    const backtestingRun =
      groupOverview?.timeframe_to_backtesting_run_data?.[180];
    tabToComponent[tabToDisplayName[GroupExtraDetailsTabs.SCORE]] = (
      <>
        {disclaimer}
        <ScoreInfo
          group={groupOverview}
          loading={getGroupOverviewQuery?.isLoading}
          hideTitle={true}
        />
      </>
    );
    tabToComponent[tabToDisplayName[GroupExtraDetailsTabs.STATISTICS]] = (
      <BacktestDetailsProvider backtestingRun={backtestingRun}>
        {disclaimer}
        <BacktestingStatistics
          loading={getGroupOverviewQuery?.isLoading}
          isGroupDrawer={true}
          style={{ padding: 0, overflowY: "unset", marginTop: 12 }}
        />
      </BacktestDetailsProvider>
    );
  }

  tabToComponent[tabToDisplayName[GroupExtraDetailsTabs.REVIEWS]] = groupId && (
    <>
      {isSmallScreen && disclaimer}
      <ReviewsTabContent subgroupId={groupId} />
    </>
  );

  const extension = <DrawerExtension isScoreHover={isScoreHover} />;

  const showEmpty =
    !getGroupOverviewQuery?.isFetching &&
    (!groupOverview || getGroupOverviewQuery?.isError);
  const emptyContent = isNewLook ? (
    <GroupsListMessage
      title={t("errors.group_title")}
      description={t("errors.description")}
      actionLabel={t("errors.retry")}
      onAction={() => getGroupOverviewQuery?.refetch()}
    />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
  );

  return (
    <ExtraDetailsDrawer
      title={title}
      visible={visible}
      tabsToComponents={tabToComponent}
      tab={tabDisplayName}
      onTabChange={onTabChange}
      extension={isSmallScreen ? undefined : extension}
      hideExtensionBorder={true}
      extensionWidth={GROUP_DRAWER_EXTENSION_WIDTH}
      path={MARKETPLACE_GROUP_PATH}
      isLoading={
        !isNewLook &&
        (getGroupOverviewQuery?.isLoading || getGroupDataQuery?.isLoading)
      }
      closeDrawer={onClose}
      // New look: the drawer's own tab bar spacing (20px) applies.
      innerContentWrapperStyle={isNewLook ? undefined : CONTENT_STYLE}
      content={showEmpty ? emptyContent : undefined}
      collapsedTitle={
        isNewLook ? (
          <GroupCompactTitle
            image={groupOverview?.image_path}
            name={groupOverview?.name}
          />
        ) : undefined
      }
    />
  );
};

const CONTENT_STYLE = { marginTop: 6 };

enum GroupExtraDetailsTabs {
  OVERVIEW = "overview",
  REVIEWS = "reviews",
  SCORE = "score",
  STATISTICS = "statistics",
}
